/* eslint-disable no-undef */
import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Tooltip } from '@mui/material';
import { Stack } from '@mui/joy';
import { Chip, LoadingSpinner } from 'src/components';
import Papa from 'papaparse';
import { nutritionColumns } from './columns';

const StyledTypography = styled(Typography)({
  fontFamily: 'Comfortaa, Now, Arial',
  fontWeight: 500,
  fontSize: '12px',
  color: '#333333',
  letterSpacing: '0.15px',
  borderBottom: 'none',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  padding: '10px 0px',
});

export const Container = styled(Stack)(({ theme }) => ({
  width: '100%',
  height: 'calc(100vh - 100px)',
  justifyContent: 'center',
  alignItems: 'center',
  display: 'flex',
  gap: 3,
}));

export const Loading = () => {
  return (
    <Container>
      <LoadingSpinner sx={{ height: '5vmin !important', width: '5vmin !important' }} />
      <Typography variant="h3" sx={{ color: '#333333' }}>
        Loading Logs
      </Typography>
    </Container>
  );
};

// format ISO date to "<Month> <Day>"
// returns "Today", "Yesterday", or "<NumDaysAgo> Days Ago" if within past week
function renderDateString(params) {
  const { row } = params;
  const dateString = row.mealTime;
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const timeDiff = today.getTime() - date.getTime();
  const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

  const options = { month: 'long', day: 'numeric', year: 'numeric' };
  const localeDateString = date.toLocaleDateString('en-US', options);

  if (date.toDateString() === today.toDateString()) {
    return <Tooltip title={localeDateString}>Today</Tooltip>;
  } else if (date.toDateString() === yesterday.toDateString()) {
    return <Tooltip title={localeDateString}>Yesterday</Tooltip>;
  } else if (dayDiff < 7) {
    return <Tooltip title={localeDateString}>{dayDiff} Days Ago</Tooltip>;
  } else {
    return localeDateString;
  }
}

// based on window width, get a width to use for symptom & medications columns
// allows those columns to get larger/smaller based on screen size
const getDynamicCellWidth = (windowWidth) => {
  return windowWidth > 1400
    ? 350
    : windowWidth > 1300
    ? 300
    : windowWidth > 1200
    ? 250
    : windowWidth > 1050
    ? 200
    : windowWidth > 950
    ? 150
    : 100;
};

const defineColumn = (headerName, field, minWidth, flex) => {
  return {
    headerName: headerName,
    field: field,
    renderHeader: (params) => {
      const { colDef } = params;
      return <StyledTypography>{colDef.headerName}</StyledTypography>;
    },
    renderCell: (params) => {
      const { row } = params;
      return <StyledTypography>{row[field]}</StyledTypography>;
    },
    csvDownload: (row) => {
      return row[field];
    },
    minWidth: minWidth ?? 80,
    flex: flex ?? 1,
  };
};

// function to get table columns for single user view
export const getSingleUserColumns = () => {
  const res = [
    {
      headerName: 'Meal Time',
      field: 'mealTime',
      defaultSort: true,
      defaultSortDesc: true,
      renderCell: renderDateString,
      minWidth: 130,
      flex: 1,
    },
    {
      headerName: 'Meal Name',
      field: 'name',
      minWidth: 130,
      flex: 1,
    },
    {
      headerName: 'Foods',
      field: 'foods',
      renderCell: (params) => {
        const { row } = params;
        return (
          <StyledTypography>
            {
              row.foods //.replaceAll('; ', '\n')
              // .map((food) => {
              //   return `${food.count} ${food.portion} ${food.name}`;
              // })
              // .join(', ')
            }
          </StyledTypography>
        );
      },
      csvDownload: (row) => {
        return `""${row.foods}""`;
        // .map((food) => {
        //   return `${food.count} ${food.portion} ${food.name}`.replaceAll(',', ';');
        // })
        // .join(' // ');
      },
      minWidth: 300,
      flex: 3,
    },
  ];
  nutritionColumns.forEach((col) => res.push(defineColumn(col, col)));
  return res;
};

// function to get table columns for all users view
export const getAllUserColumns = () => {
  let singleUserColumns = getSingleUserColumns();
  return [defineColumn('ID', 'username', 130, 1), ...singleUserColumns];
};

// columns to include in csv
const csvColumns = ['username', 'mealTime', 'name', 'foods', ...nutritionColumns];

export const downloadLogCSV = (data, filename) => {
  const fullColumns = getAllUserColumns();
  const newData = data.map((item) => {
    let obj = {};
    csvColumns.forEach((col) => {
      let column = fullColumns.find((i) => {
        return i.field === col;
      });
      obj[col] = column?.csvDownload ? column?.csvDownload(item) : item[col];
    });
    return obj;
  });

  // create header string
  const header = csvColumns.join(',');

  // create csv string
  const csvContent = [
    header,
    ...newData.map((item) =>
      csvColumns
        .map((key) => {
          const value = item[key];
          return Array.isArray(value) ? value.join(';') : value;
        })
        .join(',')
    ),
  ].join('\n');

  // download
  const csvParsed = Papa.parse(csvContent, {
    header: true, // Parse the first row as headers
    skipEmptyLines: true, // Skip any empty lines
    quoteChar: '"', // Ensure quoted fields are handled correctly
  });
  const unParsed = Papa.unparse(data, {
    quotes: true, // Ensure all fields are quoted
  });
  const blob = new Blob([unParsed], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
